import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { SOLUTION_TYPE_ORDER } from '../lib/formatters';

/** Bucket key for assets whose deployment has no solution type. */
export const UNASSIGNED_SOLUTION = 'unassigned';

export interface TripAsset {
  id: string;
  asset_id_label: string | null;
  device_name: string | null;
  make: string | null;
  model: string | null;
  serial_number: string | null;
  mac_address: string | null;
  ip_address: string | null;
  firmware_version: string | null;
  firmware_latest_known: string | null;
  os_version: string | null;
  physical_location: string | null;
  warranty_expiry: string | null;
  sla_replacement_hours: number | null;
  pci_dss_status: string | null;
  status: string | null;
  last_seen: string | null;
  /** deployments.solution_type, or UNASSIGNED_SOLUTION when the deployment has none. */
  solution_type: string;
  deployment_status: string | null;
  deployment_vendor: string | null;
}

export const ASSET_COLUMNS =
  'id, asset_id_label, device_name, make, model, serial_number, mac_address, ip_address, firmware_version, firmware_latest_known, os_version, physical_location, warranty_expiry, sla_replacement_hours, pci_dss_status, status, last_seen, deployment:deployments(id, solution_type, status, vendor)';

type AssetRow = Omit<TripAsset, 'ip_address' | 'solution_type' | 'deployment_status' | 'deployment_vendor'> & {
  ip_address: unknown;
  deployment: { id: string; solution_type: string | null; status: string | null; vendor: string | null } | null;
};

export interface TripAssetsResult {
  assets: TripAsset[];
  /** Distinct solution types present, in canonical order; UNASSIGNED_SOLUTION last. */
  solutionTypes: string[];
  byType: Record<string, TripAsset[]>;
}

/**
 * assets.status defaults to 'online' at insert time and last_seen stays null
 * until something actually observes the device. A default status with no
 * observation is not a report of health, so it is treated as "not reported".
 */
export function isStatusReported(asset: Pick<TripAsset, 'status' | 'last_seen'>): boolean {
  if (asset.last_seen) return true;
  return asset.status !== null && asset.status !== 'online';
}

export interface AssetStatusSummary {
  total: number;
  reported: Record<string, number>;
  notReported: number;
  /** Short honest label: "Status not reported", "3 online · 1 offline · 2 not reported". */
  label: string;
  hasProblem: boolean;
}

const STATUS_ORDER = ['offline', 'degraded', 'maintenance', 'decommissioned', 'online'];

export function summarizeAssetStatuses(assets: TripAsset[]): AssetStatusSummary {
  const reported: Record<string, number> = {};
  let notReported = 0;
  for (const a of assets) {
    if (isStatusReported(a)) {
      const key = a.status ?? 'unknown';
      reported[key] = (reported[key] ?? 0) + 1;
    } else {
      notReported += 1;
    }
  }
  const parts = Object.keys(reported)
    .sort((a, b) => {
      const ia = STATUS_ORDER.indexOf(a);
      const ib = STATUS_ORDER.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    })
    .map((k) => `${reported[k]} ${k.replace(/_/g, ' ')}`);
  if (notReported > 0) parts.push(`${notReported} not reported`);
  const label =
    assets.length === 0
      ? 'No assets'
      : parts.length === 1 && notReported === assets.length
        ? 'Status not reported'
        : parts.join(' · ');
  const hasProblem = (reported.offline ?? 0) > 0 || (reported.degraded ?? 0) > 0;
  return { total: assets.length, reported, notReported, label, hasProblem };
}

export function sortSolutionTypes(types: Iterable<string>): string[] {
  return Array.from(new Set(types)).sort((a, b) => {
    if (a === UNASSIGNED_SOLUTION) return 1;
    if (b === UNASSIGNED_SOLUTION) return -1;
    const ia = SOLUTION_TYPE_ORDER.indexOf(a);
    const ib = SOLUTION_TYPE_ORDER.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a.localeCompare(b);
  });
}

export function groupAssets(assets: TripAsset[]): TripAssetsResult {
  const byType: Record<string, TripAsset[]> = {};
  for (const a of assets) {
    (byType[a.solution_type] ??= []).push(a);
  }
  return { assets, solutionTypes: sortSolutionTypes(Object.keys(byType)), byType };
}

export function tripAssetsQueryKey(locationId: string | undefined) {
  return ['trip-assets', locationId] as const;
}

export function useTripAssets(locationId: string | undefined) {
  return useQuery({
    queryKey: tripAssetsQueryKey(locationId),
    enabled: !!locationId,
    queryFn: async (): Promise<TripAssetsResult> => {
      if (!locationId) throw new Error('Location ID required');
      const { data, error } = await supabase
        .from('assets')
        .select(ASSET_COLUMNS)
        .eq('location_id', locationId)
        .order('device_name', { ascending: true, nullsFirst: false });
      if (error) throw error;

      const rows = (data ?? []) as unknown as AssetRow[];
      const assets: TripAsset[] = rows.map((row) => {
        const { deployment, ip_address, ...rest } = row;
        return {
          ...rest,
          ip_address: ip_address == null ? null : String(ip_address),
          solution_type: deployment?.solution_type ?? UNASSIGNED_SOLUTION,
          deployment_status: deployment?.status ?? null,
          deployment_vendor: deployment?.vendor ?? null,
        };
      });
      return groupAssets(assets);
    },
    staleTime: 2 * 60 * 1000,
  });
}
